import React, {useState, useEffect, useRef} from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { unfolder, urlValue, useTranslate } from '../lib';

const SearchProject = ({ onProjectSelect, searchResultsContainer, showSearch, setShowSearch }) => {
    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);
    const { t } = useTranslate();
    const searchProjects = async (term) => {
        if (!term || term.length < 2) {
            setProjects([]);
            return;
        }

        try {
            setLoading(true);
            const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
            const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;

            // Use getAllProjects as a direct fallback method
            const getAllProjects = async () => {
                try {
                    const response = await axios({
                        method: 'get',
                        url: `${urlValue}/studio/project`,
                        headers: {
                            user: id,
                            'access-token': token
                        }
                    });
                    // Filter projects by name client-side
                    const filteredProjects = response.data.filter(project => 
                        project.name.toLowerCase().includes(term.toLowerCase())
                    );
                    
                    return filteredProjects || [];
                } catch (error) {
                    console.error('Error fetching all projects:', error);
                    return [];
                }
            };

            // First try with the search endpoint
            try {
                const response = await axios({
                    method: 'get',
                    url: `${urlValue}/studio/project/search`,
                    params: { 
                        searchTerm: term
                    },
                    headers: {
                        user: id,
                        'access-token': token
                    },
                    timeout: 5000 // Set a reasonable timeout
                });

                if (response.data && response.data.projects) {
                    setProjects(response.data.projects);
                } else {
                    // If no data or missing projects property, fall back
                    const allProjects = await getAllProjects();
                    setProjects(allProjects);
                }
            } catch (searchError) {
                console.warn('Search endpoint failed, falling back to all projects:', searchError);
                const allProjects = await getAllProjects();
                setProjects(allProjects);
            }
        } catch (error) {
            console.error('Error in project search:', error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                searchProjects(searchTerm);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    useEffect(() => {
        if (!showSearch) {
            setProjects([]);
            setSearchTerm('');
        }
    }, [showSearch]);

    const handleProjectSelect = (project) => {
        if (onProjectSelect) {
            onProjectSelect(project);
        }
    };

    if (!searchResultsContainer || !showSearch) return null;

    const searchResults = (
        <div id='search_project_result' ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                background: 'white',
                padding: 10,
                borderRadius: 10,
                boxShadow: '1px 1px 3px 0px rgba(0,0,0,0.75)',
                marginTop: 10,
                zIndex: 1000,
                display: showSearch ? 'block' : 'none'
            }}
        >
            <div style={{fontSize: 9, color: "#535353ff", textTransform: 'uppercase', marginTop: 5}}>
                {t('menu.searchByProjectsCampaignAndSubcampaign')}
            </div>
            <h6 style={{fontSize: 14, marginTop: 5}}>{t('menu.searchProjects')}:</h6>
            <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('menu.typeProjectName')}
                style={{
                    width: '100%',
                    padding: 10,
                    fontWeight: 400,
                    fontSize: 12,
                    borderRadius: 5,
                    marginTop: 10
                }}
                autoFocus
            />
            {loading && (
                <div style={{textAlign: 'center', padding: '10px'}}>
                    {t('common.searching')}...
                </div>
            )}
            {!loading && projects.length > 0 && (
                <div style={{
                    marginTop: 10,
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    {projects.map((project, index) => (
                        <div 
                            key={index}
                            onClick={() => handleProjectSelect(project)}
                            style={{
                                padding: '8px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                marginTop: 20
                            }}
                        >
                            
                            {(project.campaign_name || project.subcampaign_name) && (
                                <div style={{
                                    textTransform: 'uppercase',
                                    fontSize: 11,
                                    color: '#333333',
                                    backgroundColor: '#ecececff',
                                    marginBottom: 2,
                                    letterSpacing: 0.3,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: 3
                                }}>
                                    <div>
                                        <b>CAMP:</b> {project.campaign_name || 'Sin campaña'}
                                    </div>
                                    <div className='ml-1'>|</div>
                                    <div className='ml-1'>
                                        <b>SUB:</b> {project.subcampaign_name ? ` · ${project.subcampaign_name}` : ''}
                                    </div>
                                </div>
                            )}

                            <div style={{fontWeight: 500}}>
                                {project.name}
                            </div>
                            <div style={{fontSize: '12px', color: '#666'}}>
                                {t('common.id')}: {project.id}
                            </div>
                            {project.metadata && (
                                <div style={{fontSize: '12px', color: '#888'}}>
                                    {typeof project.metadata === 'string' ? 
                                        JSON.parse(project.metadata).description : 
                                        project.metadata.description}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {!loading && searchTerm && projects.length === 0 && (
                <div style={{textAlign: 'center', padding: '10px', color: '#999'}}>
                    {t('menu.noProjectsFound')} "{searchTerm}"
                </div>
            )}
        </div>
    );

    return createPortal(searchResults, searchResultsContainer);
};

export default SearchProject;